// Utilidades compartidas de los importadores V2 (Excel -> tabla editable -> servidor).
// Puras: sin Vue ni store. Las reglas de cada importador viven en su propio helper (ej. providerImportV2.js).
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { getUUIDv4 } from "@/helpers/misc";

export const PREVIEW_ROWS = 200; // los diálogos de confirmación listan solo las primeras filas

// Intencional: el sanitizer reemplaza los caracteres de control del Excel por espacio.
// eslint-disable-next-line no-control-regex
export const CONTROL_CHARS = /[\u0000-\u001F\u007F]+/g;
export const FORMULA_START = /^\s*[=+\-@]/;
// Texto libre: largo máximo y que no inicie como fórmula de Excel/CSV (mismo criterio que el DTO del backend). "" si está bien.
export const tooLong = (value, max) => (value.length > max ? `Máximo ${max} caracteres` : "");
export const freeText = (value, max) => tooLong(value, max) || (FORMULA_START.test(value) ? "No puede iniciar con =, +, - o @" : "");

export const cleanText = (value) => String(value ?? "").replace(CONTROL_CHARS, " ").trim();
// Mismo fold que el DTO del backend: sin mayúsculas, tildes ni espacios repetidos.
export const normalize = (value) => cleanText(value).normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").toLowerCase();
// Encabezado de la plantilla: se ignoran el "*" y el "(opcional)".
export const normalizeHeader = (value) => normalize(value).replace(/\*/g, "").replace(/\(opcional\)/g, "").trim();
// NIT para comparar: solo letras y números ("0614-010101-101-1" y "06140101011011" son el mismo).
export const normalizeNit = (value) => cleanText(value).replace(/[^0-9a-z]/gi, "").toLowerCase();
export const formatCount = (count) => count.toLocaleString(); // separador de miles según el idioma del navegador
export const plural = (count, one, many) => `${formatCount(count)} ${count === 1 ? one : many}`;

// Catálogo de Hacienda (Codigo/Valores): nombres, y su índice por texto (sin mayúsculas ni tildes) y por código. Los índices se congelan: Vue no los observa.
const indexBy = (items, keyOf) => {
  const map = new Map();
  items.forEach((item) => { const key = keyOf(item); if (key && !map.has(key)) map.set(key, item); }); // con textos repetidos gana el primero
  return map;
};
export const catalog = (items) => {
  const list = items.map((item) => ({ code: String(item.Codigo ?? "").trim(), text: cleanText(item.Valores) })).filter((item) => item.text);
  return { names: Object.freeze(list.map((item) => item.text)), byText: indexBy(list, (item) => normalize(item.text)), byCode: indexBy(list, (item) => item.code) };
};

// La hoja sheetName (sin mayúsculas ni tildes) si existe; si no, la primera, como filas planas. El rango va de A1 a la última celda con
// valor, no el "!ref": una hoja con formato hasta la fila 1048576 generaría un millón de filas vacías y congelaría la pantalla ~15 s.
// Empezar en A1 y blankrows: true hacen que la posición en el arreglo sea la fila real de Excel (una hoja que empieza en A3 la desfasaría).
// Limitación conocida: una celda con valor suelta en la última fila (una nota olvidada) sigue generando el millón de filas.
export function readExcelRows(file, { maxBytes, sheetName } = {}) {
  return new Promise((resolve, reject) => {
    if (maxBytes && file.size > maxBytes) {
      reject(new Error(`El archivo pesa ${(file.size / 1048576).toFixed(1)} MB; el máximo es ${Math.round(maxBytes / 1048576)} MB.`));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
    reader.onload = async (event) => {
      try {
        // Leer el Excel bloquea la pantalla: primero se deja pintar el indicador de carga.
        await new Promise((frame) => requestAnimationFrame(() => setTimeout(frame, 0)));
        const workbook = XLSX.read(new Uint8Array(event.target.result), { type: "array" });
        const wanted = normalize(sheetName);
        const sheet = workbook.Sheets[(wanted && workbook.SheetNames.find((name) => normalize(name) === wanted)) || workbook.SheetNames[0]];
        const end = Object.keys(sheet)
          .filter((key) => key[0] !== "!")
          .map((key) => XLSX.utils.decode_cell(key))
          .reduce((last, { r, c }) => ({ r: Math.max(last.r, r), c: Math.max(last.c, c) }), { r: 0, c: 0 });
        resolve(XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: true, range: { s: { r: 0, c: 0 }, e: end } }));
      } catch (error) {
        reject(new Error("No se pudo leer el archivo: no parece un Excel válido (.xlsx o .xls)."));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

// Busca la fila de encabezados por nombre (no tiene que ser la 1) y devuelve [{ excelRow, values }] con el texto limpio de
// cada columna. Las filas totalmente vacías se omiten. Lanza Error si faltan los encabezados obligatorios.
export function parseSheet(sheetRows, columns, requiredKeys) {
  const column = (key) => columns.find((item) => item.key === key);
  const headerIndex = sheetRows.findIndex((cells) => {
    const headers = cells.map((cell) => normalizeHeader(cell));
    return requiredKeys.every((key) => headers.includes(normalizeHeader(column(key).header)));
  });
  if (headerIndex < 0) {
    const names = requiredKeys.map((key) => column(key).header).join(", ");
    throw new Error(`El archivo no tiene los encabezados de la plantilla V2 (${names}). Descargue la plantilla y use sus columnas.`);
  }

  const position = {};
  sheetRows[headerIndex].forEach((cell, index) => {
    const found = columns.find((item) => normalizeHeader(item.header) === normalizeHeader(cell));
    if (found && position[found.key] === undefined) position[found.key] = index;
  });

  return sheetRows.slice(headerIndex + 1).reduce((rows, cells, offset) => {
    const values = {};
    columns.forEach((item) => { values[item.key] = position[item.key] === undefined ? "" : cleanText(cells[position[item.key]]); });
    if (Object.values(values).some((value) => value !== "")) rows.push({ excelRow: headerIndex + offset + 2, values });
    return rows;
  }, []);
}

// Plantilla .xlsx: hoja principal (encabezados, formato texto), Catalogos (listas + desplegables por rango, sin bloquear lo que
// no está en la lista) e Instrucciones. lists: [{ title, key, values }] (key = columna que recibe el desplegable).
export async function buildTemplate({ sheetName, columns, textKeys = [], lists = [], instructions = [], validationRows = 1000 }) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);
  const catalogs = workbook.addWorksheet("Catalogos");
  const guide = workbook.addWorksheet("Instrucciones");

  sheet.addRow(columns.map((column) => `${column.header}${column.required ? "*" : ""}`));
  sheet.columns = columns.map((column) => ({ width: column.width }));
  sheet.getRow(1).font = { bold: true };
  columns.forEach((column, index) => { if (textKeys.includes(column.key)) sheet.getColumn(index + 1).numFmt = "@"; }); // Excel corta ceros y números largos

  lists.forEach((list, index) => {
    catalogs.getCell(1, index + 1).value = list.title;
    list.values.forEach((value, row) => { catalogs.getCell(row + 2, index + 1).value = value; });
    const letter = catalogs.getColumn(index + 1).letter;
    catalogs.getColumn(index + 1).width = 28;
    const target = columns.findIndex((column) => column.key === list.key) + 1;
    if (!list.values.length || !target) return;
    // Una validación por rango (no por celda: ExcelJS compacta las celdas en rangos que se pisan y Excel puede pedir reparar el archivo).
    // Solo las primeras filas (miles de filas vuelven pesada la plantilla); el resto se valida al cargar.
    const column = sheet.getColumn(target).letter;
    sheet.dataValidations.add(`${column}2:${column}${validationRows + 1}`, {
      type: "list",
      allowBlank: true,
      formulae: [`'Catalogos'!$${letter}$2:$${letter}$${list.values.length + 1}`],
      showErrorMessage: false,
    });
  });
  catalogs.getRow(1).font = { bold: true };

  instructions.forEach((row) => guide.addRow(row));
  guide.getColumn(1).width = 120;
  guide.getRow(1).font = { bold: true };

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: "application/octet-stream" });
}

// Lotes consecutivos de hasta size elementos (el último puede ser menor).
export const chunk = (items, size) => Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size));

// Clave de idempotencia por lote: el mismo contenido (reintento tras una respuesta perdida) reenvía la misma clave y el
// servidor devuelve lo ya guardado; si cambia algo, la clave es nueva. keys es un Map huella -> clave (no reactivo): quien
// guarda el lote borra la huella al terminar bien, para que un lote idéntico más adelante sea otra importación.
export function batchImportKey(keys, providers) {
  const fingerprint = JSON.stringify(providers);
  if (!keys.has(fingerprint)) keys.set(fingerprint, getUUIDv4());
  return { key: keys.get(fingerprint), fingerprint };
}

// Errores del servidor { path: "providers.N.campo", message }: { rows: { N: { campo: mensaje } }, general: [mensaje] }.
// N es la posición en el lote enviado. Un error de fila sin campo se ancla en fallbackField.
export function splitServerErrors(errors, scope, fallbackField = "name") {
  const rows = {};
  const general = [];
  (errors || []).forEach(({ path, message }) => {
    const [head, index, field] = String(path).split(".");
    if (head === scope && /^\d+$/.test(index || "")) rows[index] = { ...rows[index], [field || fallbackField]: message };
    else general.push(message);
  });
  return { rows, general };
}

// Por qué rechazó el servidor, en claro: cuántas filas por motivo (los 3 más frecuentes), no solo "hay filas con errores".
export function summarizeServerErrors(errors, scope, hint = "") {
  const keysByReason = new Map();
  errors.forEach(({ path, message }) => {
    const [head, index] = String(path).split(".");
    keysByReason.set(message, (keysByReason.get(message) || new Set()).add(head === scope ? `fila ${index}` : "general"));
  });
  const reasons = [...keysByReason.entries()].sort((a, b) => b[1].size - a[1].size);
  const lines = reasons.slice(0, 3).map(([message, keys]) => (keys.has("general") && keys.size === 1 ? message : `${plural(keys.size, "fila", "filas")}: ${message}`));
  const fix = reasons.some(([, keys]) => [...keys].some((key) => key !== "general")) ? " Corrija las celdas marcadas y vuelva a guardar." : "";
  return `${lines.join(". ")}.${hint ? ` ${hint}` : ""}${fix}`;
}

// ---------- Corregir una fila (celda de la tabla o formulario) ----------
// Campos de `after` cuyo texto limpio difiere del de `before`, ya con el texto limpio: { campo: texto }. Es lo que cambió el usuario en un formulario.
export const diffFields = (before, after) => Object.fromEntries(Object.entries(after).map(([key, value]) => [key, cleanText(value)]).filter(([key, value]) => value !== cleanText(before[key])));

// Lo editado deja de depender de los errores del servidor (y de un "sin cambios" que dijo el servidor).
export function forgetServer(row, fields) {
  const serverErrors = { ...row.serverErrors };
  fields.forEach((field) => { delete serverErrors[field]; });
  row.serverErrors = serverErrors;
  if (row.existing && row.existing.by === "servidor") row.existing = null;
}

// Escribe en la fila lo editado (formulario) y lo suelta de lo que dijo el servidor, como al editar la celda. false si no había nada que escribir.
export function applyChanges(row, fields) {
  const names = Object.keys(fields);
  if (names.length) {
    Object.assign(row, fields);
    forgetServer(row, names);
  }
  return names.length > 0;
}

// Vista previa de una corrección sin tocar las filas reales: evaluate corre sobre copias de todas las filas (los repetidos del archivo dependen
// de las demás) con los cambios puestos en la copia de `row`, que es lo que devuelve. Sin cambios la fila ya está evaluada: se devuelve tal cual.
// Limitación conocida: copia y evalúa todas las filas en cada cambio (3000 filas ≈ 35 ms, medido en el check); si pesara, bastaría evaluar hasta la fila editada: los repetidos solo miran hacia arriba.
export function previewRow(rows, row, changes, evaluate) {
  if (!Object.keys(changes).length) return row;
  const copies = rows.map((item) => ({ ...item }));
  const copy = copies[rows.indexOf(row)];
  applyChanges(copy, changes);
  evaluate(copies);
  return copy;
}
